import Callout from "components/callout/callout"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { AccessRole } from 'types'
import useSuppliers, { SuppliersSortBy } from "hooks/useSuppliers"
import useStorages from "hooks/useStorages"
import { FormStatus } from "@/types"
import React from "react"
import IconImage from "components/image/IconImage"
import Section from "components/layout/section"
import { ReactComponent as SupplierIcon } from "assets/icons/shipping.svg"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import Button from "components/inputs/button"
import TableList from "components/list/tableList/tableList"
import TableListRow from "components/list/tableList/tableListRow"
import TableListCell from "components/list/tableList/tableListCell"
import useAccess from "@/hooks/useAccess"
import SupplierFilter from "./SupplierFilter"
import DocList, { ViewMode } from "components/list/docList/docList"
import Doc from "components/list/docList/doc"

interface SupplierListProps {
    storageId: string
}

const SupplierList: React.FC<SupplierListProps> = ({ storageId }) => {

    const { t } = useTranslation()

    const { storage } = useStorages({ storageId })

    const {
        suppliers,
        suppliersStatus,
        filteredSuppliers,
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        filterQuery,
        setFilterQuery,
        displayDeleted,
        setDisplayDeleted
    } = useSuppliers({ storageId })
    
    const {
        limitToRoles
    } = useAccess({ storageId })

    // Handle error state.
    if (suppliersStatus === FormStatus.ERROR) {
        return <Callout type={`error`}>{t('errorLoadingSuppliers')}</Callout>
    }

    // Handle empty state.
    if ((storage?.counters?.supplierCounter === 0 || storage?.counters?.supplierCounter === undefined) && limitToRoles([AccessRole.OWNER, AccessRole.EDITOR])) {
        return (
            <Section design={`card-centered`}>
                <IconImage>
                    <SupplierIcon />
                </IconImage>
                <h2>{t('emptyStateSuppliersTitle')}</h2>
                <p>{t('emptyStateSuppliersDescription')}</p>
                <Link className="block w-full md:inline-block md:w-fit" to={`/storage/suppliers/create`}>
                    <Button wideOnMobile design="primary">
                        <AddIcon />
                        <span>{t('buttonNewSupplier')}</span>
                    </Button>
                </Link>
            </Section>
        )
    }

    // Render the JSX.
    return (
        <DocList
            filterElement={
                <SupplierFilter
                    filterQuery={filterQuery}
                    setFilterQuery={setFilterQuery}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    sortDirection={sortDirection}
                    setSortDirection={setSortDirection}
                    allowedSortBy={["id", "name", "email", "phone"]}
                    displayDeleted={displayDeleted}
                    setDisplayDeleted={setDisplayDeleted}
                />
            }
            noResultsElement={
                <Callout type={`error`}>{t(`labelNoResults`)}</Callout>
            }
            showResults={(suppliersStatus !== `success`) || filteredSuppliers.length > 0}
            id={`suppliers-list-filter`}
            viewModes={[ViewMode.Table]}
            defaultViewMode={ViewMode.Table}
        >
            <div data-view-mode={ViewMode.Grid} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {suppliersStatus === FormStatus.SUCCESS && filteredSuppliers.length > 0
                    ? filteredSuppliers.map((supplier, key) => {    
                        const supplierId = supplier.id
                        if (!supplierId) return null
                        return (
                            <Link key={key} to={`/storage/suppliers/${supplierId}`}>
                                <Doc
                                    title={`${supplier.supplierId}: ${supplier.name}`}
                                    isDeleted={supplier.isDeleted}
                                >
                                    <div>{supplier.contactInformation?.email}</div>
                                    <div>{supplier.contactInformation?.phone}</div>
                                    {supplier.notes && <div className="text-sm text-gray-500">{supplier.notes.substring(0, 100)}...</div>}
                                </Doc>
                            </Link>
                        )
                    }) :
                    Array.from({ length: 3 }).map((_, _index) => (
                        <Doc key={_index} isLoading={true}>
                            <p className="skeleton"></p>
                            <p className="skeleton"></p>
                            <p className="skeleton"></p>
                        </Doc>
                    ))
                }
            </div>
            <div data-view-mode={ViewMode.Table}>
                <TableList>
                    <TableListRow isHeader={true}>
                        <TableListCell>{t(`labelId`)}</TableListCell>
                        <TableListCell>{t(`columnName`)}</TableListCell>
                        <TableListCell>{t(`columnEmail`)}</TableListCell>
                        <TableListCell>{t(`columnPhone`)}</TableListCell>
                    </TableListRow>
                    {suppliersStatus === FormStatus.SUCCESS && filteredSuppliers.length > 0
                        ? filteredSuppliers.map((supplier, key) => {
                            const supplierId = supplier.id
                            if (!supplierId) return null
                            return (
                                <TableListRow key={key} to={`/storage/suppliers/${supplierId}`} isDeleted={supplier.isDeleted}>
                                    <TableListCell>{supplier.supplierId}</TableListCell>
                                    <TableListCell>{supplier.name}</TableListCell>
                                    <TableListCell>{supplier.contactInformation?.email}</TableListCell>
                                    <TableListCell>{supplier.contactInformation?.phone}</TableListCell>
                                </TableListRow>
                            )
                        }) :
                        Array.from({ length: 3 }).map((_, _index) => (
                            <TableListRow key={_index}>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                            </TableListRow>
                        ))
                    }
                </TableList>
            </div>
            <div data-view-mode={ViewMode.List} className="flex flex-col gap-4">
                {suppliersStatus === FormStatus.SUCCESS && filteredSuppliers.length > 0
                    ? filteredSuppliers.map((supplier, key) => {    
                        const supplierId = supplier.id
                        if (!supplierId) return null
                        return (
                            <Link key={key} to={`/storage/suppliers/${supplierId}`}>
                                <Doc
                                    title={`${supplier.supplierId}: ${supplier.name}`}
                                    isDeleted={supplier.isDeleted}
                                >
                                    <div>{supplier.contactInformation?.email}</div>
                                    <div>{supplier.contactInformation?.phone}</div>
                                    {supplier.notes && <div className="text-sm text-gray-500">{supplier.notes.substring(0, 100)}...</div>}
                                </Doc>
                            </Link>
                        )
                    }) :
                    Array.from({ length: 3 }).map((_, _index) => (
                        <Doc key={_index} isLoading={true}>
                            <p className="skeleton"></p>
                            <p className="skeleton"></p>
                            <p className="skeleton"></p>
                        </Doc>
                    ))
                }
            </div>
        </DocList>
    )
}

export default SupplierList
